import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  Server,
  MonitorCog,
  Network,
  Phone,
  Mail,
} from "lucide-react";
import { api, asArray } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const iconMap = { ShieldCheck, Server, MonitorCog, Network };

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    setService(null);
    api
      .get(`/services/${slug}`)
      .then((r) => setService(r.data))
      .catch(() => setError(true));
    api
      .get("/services")
      .then((r) => setRelated(asArray(r.data).filter((s) => s.slug !== slug).slice(0, 3)))
      .catch(() => setRelated([]));
  }, [slug]);

  useSeo({
    title: service?.title,
    description: service?.summary,
    image: service?.image_url,
    path: `/layanan/${slug}`,
    jsonLd: service && {
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.title,
      description: service.summary || service.description,
      provider: {
        "@type": "Organization",
        name: "CV. Komunika Teknik",
      },
      areaServed: "ID",
      serviceType: service.title,
    },
  });

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
        <Header />
        <div className="flex-1 grid place-items-center kt-section" data-testid="service-not-found">
          <div className="text-center">
            <div className="kt-eyebrow">404</div>
            <h1 className="font-display text-3xl font-bold mt-4">
              Layanan tidak ditemukan
            </h1>
            <button
              onClick={() => navigate("/layanan")}
              className="kt-btn-primary mt-6"
            >
              <ArrowLeft size={16} /> Kembali ke Layanan
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
        <Header />
        <div className="flex-1 grid place-items-center text-[#52525B] font-mono-kt text-sm">
          Memuat…
        </div>
      </div>
    );
  }

  const Icon = iconMap[service.icon] || Server;

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="service-detail-page">
      <Header />

      {/* HERO */}
      <section className="bg-[#0F172A] text-white pt-24 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={service.image_url}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-transparent" />
        </div>
        <div className="kt-container relative">
          <Link
            to="/layanan"
            className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-2 mb-6"
            data-testid="service-detail-back-link"
          >
            <ArrowLeft size={14} /> Semua Layanan
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-[#DC2626] grid place-items-center">
              <Icon size={24} />
            </div>
            <div className="kt-eyebrow text-[#FCA5A5]">Layanan</div>
          </div>

          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            {service.title}
          </h1>
          {service.summary && (
            <p className="mt-6 text-zinc-300 text-lg max-w-3xl leading-relaxed">
              {service.summary}
            </p>
          )}
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-24">
        <div className="kt-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="kt-eyebrow">Tentang Layanan</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight">
              Ruang Lingkup & Pendekatan
            </h2>
            <div className="kt-divider-red mt-5" />
            <p className="mt-6 text-[#18181B] leading-relaxed text-base md:text-lg whitespace-pre-wrap">
              {service.description}
            </p>

            {service.features?.length > 0 && (
              <div className="mt-12">
                <div className="kt-eyebrow">Yang Anda Dapatkan</div>
                <h3 className="font-display text-xl md:text-2xl font-bold mt-3">
                  Komponen utama layanan
                </h3>
                <div className="kt-divider-red mt-5" />
                <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 p-5 border border-[#E4E4E7] bg-white"
                      data-testid={`service-feature-${f.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                    >
                      <CheckCircle2
                        size={20}
                        className="text-[#DC2626] mt-0.5 shrink-0"
                      />
                      <span className="text-sm md:text-base font-medium text-[#18181B]">
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-[#0F172A] text-white p-7" data-testid="service-detail-cta-card">
              <div className="kt-eyebrow text-[#FCA5A5]">Mulai Sekarang</div>
              <h3 className="font-display text-xl font-bold mt-3 leading-tight">
                Konsultasikan kebutuhan Anda
              </h3>
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                Tim kami siap menyusun proposal khusus sesuai dengan skala dan
                urgensi proyek.
              </p>
              <Link
                to="/kontak"
                className="kt-btn-primary mt-6 w-full justify-center"
                data-testid="service-detail-cta-button"
              >
                Minta Penawaran <ArrowRight size={16} />
              </Link>

              <div className="mt-8 pt-6 border-t border-white/10 space-y-3 text-sm">
                <a
                  href="mailto:info@komunikateknik.online"
                  className="flex items-center gap-3 text-zinc-300 hover:text-white"
                >
                  <Mail size={16} className="text-[#DC2626]" />
                  info@komunikateknik.online
                </a>
                <a
                  href="tel:+6285246286550"
                  className="flex items-center gap-3 text-zinc-300 hover:text-white"
                >
                  <Phone size={16} className="text-[#DC2626]" />
                  +62 852-4628-6550
                </a>
              </div>
            </div>

            <div className="kt-card p-6">
              <div className="kt-eyebrow">Keunggulan</div>
              <ul className="mt-5 space-y-3 text-sm text-[#18181B]">
                {[
                  "Engineer bersertifikasi",
                  "Lisensi & perangkat resmi",
                  "Dokumentasi lengkap",
                  "Support purna jual",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <span className="w-1 h-1 bg-[#DC2626]" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="kt-section bg-white border-t border-[#E4E4E7]">
          <div className="kt-container">
            <div className="kt-eyebrow">Layanan Lainnya</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mt-3">
              Eksplorasi pilar layanan lainnya
            </h2>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((s) => {
                const RIcon = iconMap[s.icon] || Server;
                return (
                  <Link
                    key={s.id}
                    to={`/layanan/${s.slug}`}
                    className="kt-card p-7 group"
                    data-testid={`related-service-${s.slug}`}
                  >
                    <div className="w-12 h-12 bg-[#18181B] text-white grid place-items-center group-hover:bg-[#DC2626] transition-colors">
                      <RIcon size={20} />
                    </div>
                    <h3 className="font-display font-bold text-lg mt-5 leading-snug">
                      {s.title}
                    </h3>
                    <p className="text-sm text-[#52525B] mt-2 line-clamp-3">
                      {s.summary}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ServiceDetail;
